import { Identity, Maybe } from '@vendorflow/types'
import { channelQueryKey, channelsQueryKey } from 'queries/Channels.queries'
import { QueryCacheEnum } from 'constants/ReactQuery.constants'
import { useQueryClient } from '@tanstack/react-query'
import { useMyIdentityId, useMyOrgId } from 'queries/Auth.queries'
import { alicesForEnvyQueryKey, bobsInEnvyQueryKey } from 'queries/Organizations.queries'
import produce from 'immer'
import { useCallback } from 'react'

/**
 * Used for invalidating channel cache state
 */
export function useInvalidateChannelsQueries() {
  const queryClient = useQueryClient()
  const bob = useMyIdentityId()
  const envy = useMyOrgId()

  return async ({ channel, singleOnly = false }: { channel?: Maybe<string>; singleOnly?: boolean }) => {
    await queryClient.invalidateQueries(alicesForEnvyQueryKey(envy))
    if (!singleOnly) {
      await queryClient.invalidateQueries(channelsQueryKey(QueryCacheEnum.IDENTITIES, bob, envy), { exact: true })
      await queryClient.invalidateQueries(channelsQueryKey(QueryCacheEnum.ORGANIZATIONS, envy), { exact: true })
    }
    if (channel) {
      await queryClient.invalidateQueries(channelQueryKey(channel), { exact: true })
    }
  }
}

export function useInvalidateContactGroupsQueries() {
  const queryClient = useQueryClient()

  return async () =>
    queryClient.invalidateQueries({
      predicate(query) {
        return (
          query.queryKey.includes(QueryCacheEnum.VENDOR_GROUPS) ||
          query.queryKey.includes(QueryCacheEnum.VENDOR_GROUPS_WITH_MEMBERS)
        )
      },
    })
}

export function useUpdateContactsCacheForAlice() {
  const envy = useMyOrgId()
  const queryClient = useQueryClient()

  return useCallback((alice: Identity) => {
    if (!envy) {
      throw new Error('Your organization cannot be found.')
    }

    queryClient.setQueryData(alicesForEnvyQueryKey(envy), (oldData: Identity[] | undefined): Identity[] | undefined =>
      produce(oldData, (draft) => {
        if (!draft) {
          draft = []
        }
        const index = draft.findIndex((a) => a.id === alice.id)
        if (index > -1) {
          draft[index] = alice
        } else {
          draft.push(alice)
        }
      })
    )
  }, [])
}

export function useUpdateMembersCacheForBob() {
  const envy = useMyOrgId()
  const queryClient = useQueryClient()

  return useCallback((bob: Identity) => {
    if (!envy) {
      throw new Error('Your organization cannot be found.')
    }

    queryClient.setQueryData(bobsInEnvyQueryKey(envy), (oldData: Identity[] | undefined): Identity[] | undefined =>
      produce(oldData, (draft) => {
        if (!draft) {
          draft = []
        }
        const index = draft.findIndex((a) => a.id === bob.id)
        if (index > -1) {
          draft[index] = bob
        } else {
          draft.push(bob)
        }
      })
    )
  }, [])
}
